import CryptoJS from "crypto-js";

// Get the secret key from environment variable
const getSecretKey = (): string => {
  const secretKey = process.env.REACT_APP_CRYPTO_ENCRYPT_DECRYPT_KEY;
  
  if (!secretKey) {
    console.error("CRYPTO_ENCRYPT_DECRYPT_KEY environment variable is not set");
    // Fallback to a default key (not recommended for production)
    return "default-secret-key-for-development";
  }
  
  return secretKey;
};

/**
 * Encrypts plain text using AES encryption
 * @param plainText - The text to encrypt
 * @returns The encrypted text
 */
export const encrypt = (plainText: string): string => {
  try {
    if (!plainText) {
      return "";
    }
    
    const secretKey = getSecretKey();
    const cipherText = CryptoJS.AES.encrypt(plainText, secretKey).toString();
    return cipherText;
  } catch (error) {
    console.error("Encryption failed:", error);
    return "";
  }
};

/**
 * Decrypts encrypted text using AES decryption
 * @param cipherText - The encrypted text to decrypt
 * @returns The decrypted text
 */
export const decrypt = (cipherText: string): string => {
  try {
    if (!cipherText) {
      return "";
    }
    
    const secretKey = getSecretKey();
    const plainText = CryptoJS.AES.decrypt(cipherText, secretKey).toString(
      CryptoJS.enc.Utf8
    );
    return plainText;
  } catch (error) {
    console.error("Decryption failed:", error);
    return "";
  }
};

/**
 * Safely stores encrypted password in localStorage
 * @param key - The localStorage key
 * @param password - The password to encrypt and store
 */
export const storeEncryptedPassword = (key: string, password: string): void => {
  try {
    if (password) {
      const encryptedPassword = encrypt(password);
      localStorage.setItem(key, encryptedPassword);
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error("Failed to store encrypted password:", error);
  }
};

/**
 * Safely retrieves and decrypts password from localStorage
 * @param key - The localStorage key
 * @returns The decrypted password or empty string
 */
export const getEncryptedPassword = (key: string): string => {
  try {
    const encryptedPassword = localStorage.getItem(key);
    if (encryptedPassword) {
      return decrypt(encryptedPassword);
    }
    return "";
  } catch (error) {
    console.error("Failed to retrieve encrypted password:", error);
    return "";
  }
};

/**
 * Removes encrypted password from localStorage
 * @param key - The localStorage key
 */
export const removeEncryptedPassword = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Failed to remove encrypted password:", error);
  }
};
